import { Autocomplete, type AutocompleteProps, TextField, type TextFieldProps } from '@mui/material';
import { forwardRef } from 'react';
import { LabeledField } from '../LabeledField';

/**
 * @tier molecule
 *
 * Single-select dropdown built on MUI Autocomplete with a stacked label.
 * Generic over the option type. The label sits above the field — not
 * floating inside the outline.
 *
 * For multi-select, use a different primitive (out of scope here).
 */
export type VySelectProps<TOption> = Omit<
  AutocompleteProps<TOption, false, false, false>,
  'renderInput' | 'multiple' | 'freeSolo' | 'disableClearable'
> & {
  /** Field label rendered above the dropdown. */
  label?: string;
  /** Placeholder shown when no option is selected. */
  placeholder?: string;
  /** Hint shown below the input. Becomes the error message when `error` is true. */
  helperText?: string;
  /** Renders the input in the error state. */
  error?: boolean;
  /** Forwarded to the input's slot props. */
  inputSlotProps?: TextFieldProps['slotProps'];
  /** Hides the clear (✕) button so a required selection can't be cleared to null. Default false. */
  disableClearable?: boolean;
};

function VySelectInner<TOption>(
  { label, placeholder, helperText, error, inputSlotProps, disableClearable, ...rest }: VySelectProps<TOption>,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <LabeledField label={label} helperText={helperText} error={error}>
      <Autocomplete<TOption, false, false, false>
        ref={ref}
        size="small"
        fullWidth
        // Cast: the third Autocomplete generic is pinned to `false` to keep
        // onChange's `value` nullable; the runtime prop is independent of it.
        disableClearable={disableClearable as false | undefined}
        {...rest}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={error}
            slotProps={{ ...params.slotProps, ...inputSlotProps }}
          />
        )}
      />
    </LabeledField>
  );
}

export const VySelect = forwardRef(VySelectInner) as <TOption>(
  props: VySelectProps<TOption> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof VySelectInner>;
